import React from 'react';
import { Button, Dropdown, Layout, Menu, Modal, Tooltip, Typography } from 'antd';

import Icon, {
  CaretDownOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FormOutlined,
  InboxOutlined,
  LeftOutlined,
  LinkOutlined,
  SettingOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';

import { Link, useHistory, useParams } from 'react-router-dom';

import './style.less';
import { useDispatch, useSelector } from 'react-redux';
import { restoreForm } from '../../store/actions/formActions';

import { useTranslation } from 'react-i18next';

import { useMediaQuery } from 'react-responsive';
import { getFormLink } from '../../utils/helpers';
import { deviceWidth } from '../../utils/media-query';

const { Header } = Layout;
const { confirm } = Modal;

const FormActionHeader = ({
  activeForm,
  previewMode,
  isPublished,
  showPreview,
  hidePreview,
  publishFormQuestions,
  isPublishLoading,
}) => {
  const dispatch = useDispatch();
  const { section } = useParams();
  const { t } = useTranslation(['builder', 'translation']);
  const { me } = useSelector((state) => state.auth);

  const isMdScreen = useMediaQuery({ maxWidth: 992 });
  const isLgScreen = useMediaQuery({ minWidth: 1280 });
  const isSmScreen = useMediaQuery({ maxWidth: 576 });

  const isBuildPage = !section || section === 'build';

  const handleRestoreForm = (e) => {
    if (!e && !e.key) return;
    if (e.key === 'restore') {
      confirm({
        title: t(
          'actionHeader.restore.confirmTitle',
          `Do you want to discard unpublished changes?`,
        ),
        icon: <ExclamationCircleOutlined />,
        content: t(
          'actionHeader.restore.confirmContent',
          "You'll lose any changes made since you last hit Publish. Are you sure want to restore the last version and discard changes?",
        ),
        okText: t('translation:common.ok', 'OK'),
        cancelText: t('translation:common.cancel', 'Cancel'),
        okButtonProps: { type: 'primary' },
        onOk() {
          return dispatch(restoreForm(activeForm.id));
        },
      });
    }
  };

  const menu = (
    <Menu onClick={handleRestoreForm}>
      <Menu.Item key="restore">
        {t('actionHeader.restore.title', 'Restore to last published version')}
      </Menu.Item>
    </Menu>
  );

  const navigationTabs = (
    <div className="action-header-navigation-tabs" style={{ display: 'flex' }}>
      <Link to={`/forms/${activeForm.id}`} className={isBuildPage ? ' active' : ''}>
        <Button
          type="text"
          className="action-button"
          icon={<FormOutlined style={{ fontSize: isSmScreen ? '1.1rem' : '0.9rem' }} />}
        >
          {!isSmScreen ? t('navigation.build') : <></>}
        </Button>
      </Link>

      <Link to={`/forms/${activeForm.id}/share`} className={section === 'share' ? 'active' : ''}>
        <Button
          type="text"
          className="action-button"
          icon={<ShareAltOutlined style={{ fontSize: isSmScreen ? '1.25rem' : '1rem' }} />}
        >
          {!isSmScreen ? t('actionHeader.actions.share') : <></>}
        </Button>
      </Link>
      <Link
        to={`/forms/${activeForm.id}/responses`}
        className={section === 'responses' ? 'active' : ''}
      >
        <Button
          type="text"
          className="action-button"
          icon={<InboxOutlined style={{ fontSize: isSmScreen ? '1.25rem' : '1rem' }} />}
        >
          {!isSmScreen ? t('navigation.responses') : <></>}
        </Button>
      </Link>

      <Link
        to={`/forms/${activeForm.id}/settings`}
        className={section === 'settings' ? 'active settings-nav' : 'settings-nav'}
      >
        <Button
          type="text"
          className="action-button"
          icon={<SettingOutlined style={{ fontSize: isSmScreen ? '1.25rem' : '1rem' }} />}
        >
          {' '}
          {!isSmScreen ? t('actionHeader.actions.settings') : <></>}
        </Button>
      </Link>
    </div>
  );

  const isMobile = useMediaQuery({ maxWidth: deviceWidth.mobile });

  return (
    <Header className="Action-header">
      {!isMobile && <div className="action-header-left-container">{navigationTabs}</div>}
      {isMdScreen && isBuildPage && (
        <div className="action-header-right-container">
          {!previewMode ? (
            <>
              <Dropdown.Button
                overlay={menu}
                type="primary"
                disabled={isPublished}
                onClick={publishFormQuestions}
                className="action-button"
                trigger="click"
                buttonsRender={([leftButton, rightButton]) => [
                  React.cloneElement(leftButton, { loading: isPublishLoading }),
                  React.cloneElement(rightButton, {
                    disabled: isPublishLoading,
                    icon: <CaretDownOutlined />,
                  }),
                ]}
              >
                {t('actionHeader.actions.publish', 'Publish')}
              </Dropdown.Button>
              <Button
                onClick={() => {
                  window.open(
                    `${getFormLink(
                      me?.customDomain,
                      activeForm.link,
                      activeForm.customDomainLink,
                    )}`,
                    '_blank',
                  );
                }}
                icon={<LinkOutlined />}
                className="action-button"
              >
                {t('actionHeader.actions.open', 'Open')}
              </Button>
              <Button onClick={showPreview} icon={<EyeOutlined />} className="action-button">
                Preview
              </Button>
            </>
          ) : (
            <Button onClick={hidePreview} type="primary" danger className="exit-preview-button">
              {t('actionHeader.actions.exit', 'Exit Preview')}
            </Button>
          )}
        </div>
      )}
      {!isMdScreen && (
        <div className="action-header-right-container">
          {!previewMode ? (
            <>
              {isBuildPage && isLgScreen && !isPublished && (
                <Tooltip
                  placement="bottom"
                  title={t(
                    'actionHeader.unpublishedChangesTooltip',
                    'You have some unpublished changes. Click Publish to make the changes live.',
                  )}
                >
                  <Button type="link" className="unpublished-changes-button">
                    <em>{t('actionHeader.unpublishedChanges', 'Unpublished changes')}</em>
                  </Button>
                </Tooltip>
              )}
              {isBuildPage && (
                <Dropdown.Button
                  overlay={menu}
                  type="primary"
                  disabled={isPublished}
                  onClick={publishFormQuestions}
                  className="action-button"
                  trigger="click"
                  buttonsRender={([leftButton, rightButton]) => [
                    React.cloneElement(leftButton, { loading: isPublishLoading }),
                    React.cloneElement(rightButton, {
                      disabled: isPublishLoading,
                      icon: <CaretDownOutlined />,
                    }),
                  ]}
                >
                  {t('actionHeader.actions.publish', 'Publish')}
                </Dropdown.Button>
              )}
              {isBuildPage && (
                <Button onClick={showPreview} icon={<EyeOutlined />} className="action-button">
                  Preview
                </Button>
              )}
              <Button
                onClick={() => {
                  window.open(
                    `${getFormLink(
                      me?.customDomain,
                      activeForm.link,
                      activeForm.customDomainLink,
                    )}`,
                    '_blank',
                  );
                }}
                icon={<LinkOutlined />}
                className="action-button"
              >
                {t('actionHeader.actions.open', 'Open')}
              </Button>

              <Link to={`/forms/${activeForm.id}/settings`} className="settings-link">
                <Button
                  className={section === 'settings' ? 'action-button active' : 'action-button'}
                  icon={<SettingOutlined />}
                >
                  {t('actionHeader.actions.settings')}
                </Button>
              </Link>
            </>
          ) : (
            <Button onClick={hidePreview} type="primary" danger className="exit-preview-button">
              {t('actionHeader.actions.exit', 'Exit Preview')}
            </Button>
          )}
        </div>
      )}
    </Header>
  );
};

export const SecondaryNavbar = ({ name }) => {
  const history = useHistory();

  const goBack = () => {
    history.goBack();
  };

  const { t } = useTranslation();

  return (
    <Header className="Action-header generic-action-header">
      <div className="left-container">
        <Button icon={<LeftOutlined />} onClick={goBack} type="text" />
        <div className="text-content">
          <Typography.Text className="sub-title title" title="Account">
            {name}
          </Typography.Text>
        </div>
      </div>
    </Header>
  );
};

export default FormActionHeader;
